import React from 'react'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { GridRow, GridColumn, Grid } from 'semantic-ui-react'
import _ from 'lodash'

import useMediaQuery from 'hooks/useMediaQuery'
import { getExternalLinks } from 'stores/Links/Select'
import { getDependentDetails } from 'stores/Account/Select'
import { useNotificationBadgesQuery } from 'hooks/api/notifications'

import BillPay from 'images/custom/images/quickActions/eh-icon-billpay.svg'
import SupportTicket from 'images/custom/images/quickActions/eh-icon-edit.svg'
import FindCare from 'images/custom/images/quickActions/eh-icon-locations.svg'
import Messages from 'images/custom/images/quickActions/eh-icon-messages.svg'
import MyHealth from 'images/custom/images/quickActions/eh-icon-my-health.svg'

import { useTheme } from 'contexts/ThemeProvider'

import themeStyles from './styles'
import localization from './localization'

import MessageNotificationBadge from 'components/Common/MessageNotificationBadge'
import withLocalization from '@web/helpers/localizationUtils'

const QuickActionItem = props => {
  const router = useRouter()
  const { theme } = useTheme()
  const defaultStyles = themeStyles(theme)
  const {
    path,
    text,
    style,
    isExternal = false,
    onClick,
    t,
    unreadCount = 0
  } = props
  const { isMobile } = useMediaQuery()
  const onClickHandler = path => {
    if (onClick) {
      onClick()
    } else {
      router.push(path)
    }
  }

  const containerStyle = {
    ...defaultStyles.quickActionImg,
    ...(isMobile && { alignItems: 'center' })
  }

  return (
    <div style={containerStyle}>
      {!isExternal ? (
        <button
          style={defaultStyles.quickActionItem}
          onClick={() => onClickHandler(path)}
          tabIndex={0}
        >
          {text === 'messages' && (
            <MessageNotificationBadge count={unreadCount}>
              <Messages style={style} />
            </MessageNotificationBadge>
          )}
          {text === 'locations' && <FindCare style={style} />}
          {text === 'myHealth' && <MyHealth style={style} />}
          <div style={defaultStyles.quickActionItemText(isMobile)}>
            {t(text)}
          </div>
        </button>
      ) : (
        <a
          style={defaultStyles.quickActionItem}
          href={path}
          target='_blank'
          rel='noreferrer'
        >
          {text === 'billPay' && <BillPay style={style} />}
          {text === 'supportTicket' && <SupportTicket style={style} />}
          <div style={defaultStyles.quickActionItemText(isMobile)}>
            {t(text)}
          </div>
        </a>
      )}
    </div>
  )
}

const QuickActionBar = ({ t }) => {
  const { isMobile } = useMediaQuery()
  const { theme } = useTheme()
  const defaultStyles = themeStyles(theme)
  const styles = {
    ...defaultStyles.container,
    ...(isMobile && defaultStyles.noPadding)
  }
  const links = _.merge({}, ...Object.values(useSelector(getExternalLinks)))
  const dependentData = useSelector(getDependentDetails)
  const dependentIds = (dependentData || [])
    .map(item => item?.patientId)
    .filter(Boolean)
  const { data: messagesUnreadCount = 0 } = useNotificationBadgesQuery({
    patientIds: dependentIds
  })

  const ACTIONS_LIST = [
    {
      src: 'messages',
      text: 'messages',
      path: '/messages',
      style: defaultStyles.actionItemWithMarginBottom,
      unreadCount: messagesUnreadCount
    },
    {
      src: 'findCare',
      text: 'locations',
      path: '/locations',
      style: defaultStyles.actionItemWithMarginBottom
    },
    {
      src: 'billPay',
      text: 'billPay',
      path: _.get(links, 'UFD_Pay_My_Bill'),
      style: defaultStyles.actionItem,
      isExternal: true
    },
    {
      src: 'supportTicket',
      text: 'supportTicket',
      path: _.get(links, 'UFD_Submit_Ticket'),
      style: defaultStyles.actionItem,
      isExternal: true
    },
    {
      src: 'myHealth',
      text: 'myHealth',
      path: '/myHealth',
      style: defaultStyles.actionItem
    }
  ]

  const getColumnWidth = () => (isMobile ? 5 : 3)

  return (
    <div style={defaultStyles.quickActionArea}>
      <div style={defaultStyles.barTitle}>{t('quick_action_title')}</div>
      <Grid style={styles}>
        <GridRow style={defaultStyles.row(isMobile)}>
          {ACTIONS_LIST.map((item, index) => (
            <GridColumn width={getColumnWidth(index)} key={item.text}>
              <QuickActionItem {...item} t={t} style={defaultStyles.images} />
            </GridColumn>
          ))}
        </GridRow>
      </Grid>
    </div>
  )
}

export default withLocalization(QuickActionBar, localization)
